import { useMemo, useState } from "react";
import { ArrowLeftRight, GitCompare, Minus, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, LifecycleChip } from "@/components/common/Chrome";
import { CASCADE_LABELS } from "@/lib/format";

const SCALAR_FIELDS = [
  ["title", "Title"],
  ["summary", "Summary"],
  ["severity", "Blocking severity"],
  ["lifecycle_state", "Lifecycle state"],
  ["jurisdiction_country", "Jurisdiction"],
  ["sub_jurisdiction", "Sub-jurisdiction"],
  ["regulator", "Regulator"],
  ["citation", "Citation"],
  ["obligation", "Obligation"],
  ["remediation", "Remediation"],
  ["effective_from", "Effective from"],
  ["effective_to", "Effective to"],
  ["evidence_status", "Evidence status"],
  ["is_global_control", "Global control"],
  ["analyst_reviewed_by", "Analyst reviewer"],
  ["legal_reviewed_by", "Legal reviewer"],
  ["approved_by", "Approved by"],
];

const show = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (Array.isArray(v)) return v.join(" | ");
  return String(v);
};

function conditionKey(c) {
  return `${c.field}:${c.operator}:${JSON.stringify(c.value)}`;
}

export const VersionDiff = ({ versions, currentId }) => {
  const sorted = useMemo(() => [...versions].sort((a, b) => b.version - a.version), [versions]);
  const current = sorted.find((v) => v.id === currentId) || sorted[0];
  const older = sorted.find((v) => v.id !== current?.id) || current;

  const [leftId, setLeftId] = useState(older?.id);
  const [rightId, setRightId] = useState(current?.id);

  const left = sorted.find((v) => v.id === leftId) || sorted[0];
  const right = sorted.find((v) => v.id === rightId) || sorted[0];

  const scalarRows = SCALAR_FIELDS.map(([key, label]) => ({
    key, label, a: left?.[key], b: right?.[key],
    changed: show(left?.[key]) !== show(right?.[key]),
  }));

  const layerKeys = Array.from(new Set([
    ...Object.keys(left?.applicability || {}), ...Object.keys(right?.applicability || {}),
  ]));
  const layerRows = layerKeys.map((key) => ({
    key, label: CASCADE_LABELS[key] || key,
    a: left?.applicability?.[key], b: right?.applicability?.[key],
    changed: show(left?.applicability?.[key]) !== show(right?.applicability?.[key]),
  }));

  const leftConds = (left?.conditions || []).map(conditionKey);
  const rightConds = (right?.conditions || []).map(conditionKey);
  const condRows = [
    ...(left?.conditions || []).map((c) => ({
      c, state: rightConds.includes(conditionKey(c)) ? "same" : "removed",
    })),
    ...(right?.conditions || []).filter((c) => !leftConds.includes(conditionKey(c)))
      .map((c) => ({ c, state: "added" })),
  ];

  const changeCount = scalarRows.filter((r) => r.changed).length
    + layerRows.filter((r) => r.changed).length
    + condRows.filter((r) => r.state !== "same").length;

  if (sorted.length < 2) {
    return (
      <Button variant="outline" size="sm" disabled data-testid="compare-versions-button"
              className="rounded-full border-border text-xs">
        <GitCompare className="mr-1.5 h-3 w-3" /> Compare versions
      </Button>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="compare-versions-button"
                className="rounded-full border-border text-xs">
          <GitCompare className="mr-1.5 h-3 w-3" /> Compare versions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-border bg-card">
        <DialogHeader>
          <DialogTitle>Version diff — {right?.rule_key}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Reviewers must see exactly what changed before approving. Versions are immutable, so a diff
            is the record of what a reviewer signed off (FR-REG-002 / FR-REG-006).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3 rounded border border-border bg-muted/40 p-3">
          <div className="flex-1">
            <div className="kicker mb-1.5">Base version</div>
            <Select value={leftId} onValueChange={setLeftId}>
              <SelectTrigger data-testid="diff-base-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sorted.map((v) => (
                  <SelectItem key={v.id} value={v.id}>v{v.version} · {v.lifecycle_state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ArrowLeftRight className="mt-5 h-4 w-4 shrink-0 text-muted-foreground/70" />
          <div className="flex-1">
            <div className="kicker mb-1.5">Compare version</div>
            <Select value={rightId} onValueChange={setRightId}>
              <SelectTrigger data-testid="diff-compare-select" className="h-8 bg-muted/40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sorted.map((v) => (
                  <SelectItem key={v.id} value={v.id}>v{v.version} · {v.lifecycle_state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4">
            <Chip testId="diff-change-count"
                  className={changeCount === 0
                    ? "border-border bg-muted text-muted-foreground"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
              {changeCount === 0 ? "identical" : `${changeCount} change${changeCount === 1 ? "" : "s"}`}
            </Chip>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded border border-border bg-muted/40 px-3 py-2">
            <div className="kicker">v{left?.version}</div>
            <LifecycleChip state={left?.lifecycle_state} />
          </div>
          <div className="rounded border border-border bg-muted/40 px-3 py-2">
            <div className="kicker">v{right?.version}</div>
            <LifecycleChip state={right?.lifecycle_state} />
          </div>
        </div>

        <Section title="Rule attributes">
          {scalarRows.map((r) => (
            <Row key={r.key} label={r.label} a={show(r.a)} b={show(r.b)} changed={r.changed}
                 testId={`diff-row-${r.key}`} />
          ))}
        </Section>

        <Section title="Applicability cascade">
          {layerRows.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground/70">No layers set on either version</div>}
          {layerRows.map((r) => (
            <Row key={r.key} label={r.label} a={show(r.a)} b={show(r.b)} changed={r.changed}
                 testId={`diff-layer-${r.key}`} />
          ))}
        </Section>

        <Section title="Trigger conditions">
          {condRows.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground/70">Applicability-only rule</div>}
          {condRows.map(({ c, state }, i) => (
            <div key={`${conditionKey(c)}-${i}`}
                 data-testid={`diff-condition-${state}-${c.field}`}
                 className={`flex items-center gap-2 px-3 py-2 font-mono text-[11px] ${
                   state === "added" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                     : state === "removed" ? "bg-red-500/10 text-red-700 dark:text-red-300" : "text-muted-foreground"}`}>
              {state === "added" ? <Plus className="h-3 w-3" />
                : state === "removed" ? <Minus className="h-3 w-3" />
                : <span className="w-3 text-center text-muted-foreground/70">·</span>}
              {c.field} {c.operator} {JSON.stringify(c.value)}
            </div>
          ))}
        </Section>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded border border-border">
    <div className="kicker border-b border-border px-3 py-2">{title}</div>
    <div className="divide-y divide-border">{children}</div>
  </div>
);

const Row = ({ label, a, b, changed, testId }) => (
  <div data-testid={testId}
       className={`grid grid-cols-[150px_1fr_1fr] gap-3 px-3 py-2 text-[11px] ${changed ? "bg-amber-500/10" : ""}`}>
    <div className="text-muted-foreground">{label}</div>
    <div className={changed ? "text-red-700 dark:text-red-300 line-through decoration-red-700/60" : "text-muted-foreground"}>{a}</div>
    <div className={changed ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}>{b}</div>
  </div>
);
